import { render } from "solid-js/web";
import { App } from "./App";
import { reportErrors } from "./net/report";
import "./tokens.css";
import "./styles.css";

reportErrors();

// The service worker (design 11 §8): the app from its cache on later visits. A release it has installed
// meanwhile takes effect from the next load: this page, just started, keeps the files it has.
// Registered once the page has loaded and is idle: a first visit's own downloads come first.
const whenIdle = (f: () => void) => (typeof requestIdleCallback === "function" ? requestIdleCallback(f, { timeout: 5000 }) : setTimeout(f, 2000));
// The development server serves no worker: one left from a build on the same address would serve that build.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => void r.unregister()));
}
// A page's code that cannot be loaded (a release's file gone and not kept by the worker) is recovered from
// once: the worker is let go and the page loads afresh from the server.
const RECOVERED = "mj-recovered";
function recover(what: unknown): void {
  if (!/dynamically imported module|Importing a module script failed/i.test(String(what))) return;
  try {
    if (sessionStorage.getItem(RECOVERED)) return;
    sessionStorage.setItem(RECOVERED, "1");
  } catch {
    return;
  }
  void (navigator.serviceWorker?.getRegistrations() ?? Promise.resolve([]))
    .then((rs) => Promise.all(rs.map((r) => r.unregister())))
    .finally(() => location.reload());
}
addEventListener("unhandledrejection", (e) => recover(e.reason));
addEventListener("error", (e) => recover(e.error ?? e.message));
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  if (document.readyState === "complete") whenIdle(register);
  else addEventListener("load", () => whenIdle(register), { once: true });
}
function register(): void {
  navigator.serviceWorker.register("/sw.js").then(
    (reg) => {
      const next = () => reg.waiting?.postMessage("next-visit");
      next();
      reg.addEventListener("updatefound", () => reg.installing?.addEventListener("statechange", () => void (reg.waiting && !inGame() && next())));
    },
    () => {},
  );
}
/** A table in play: a release found during it waits for the next load. */
const inGame = () => location.pathname.startsWith("/t/");

// ?fps=full: the CSS effects at about 120 steps a second, not 30, kept by this browser until ?fps=30 (tokens.css; design 15
// §16.1 step 23: to compare the two on a high-refresh phone).
try {
  const fps = new URLSearchParams(location.search).get("fps");
  if (fps === "full") localStorage.setItem("mj-fps", "full");
  else if (fps === "30") localStorage.removeItem("mj-fps");
  if (localStorage.getItem("mj-fps") === "full") document.documentElement.dataset.fps = "full";
} catch {
  /* no storage: 30 */
}

// The table fixture (/dev/table, design 11 §9) stands in for the table page, which is in the first download:
// loaded before the first render, as that page is, so the budget measures the table and not a lazy page.
const fixture = location.pathname === "/dev/table" ? (await import("./pages/TableFixture")).TableFixture : undefined;

// The first render in a task of its own, after the scripts' own evaluation: together they made one task over
// the budget of 50 ms on a slow phone (design 11 §3).
setTimeout(() => render(() => <App fixture={fixture} />, document.getElementById("root")!), 0);
